import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { makeStyles, Text, Button, Spinner, Field } from '@fluentui/react-components';
import { ArrowLeftRegular, CheckmarkCircleRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate, SearchableDropdown } from '../../../components/common';
import {
  ApiClient,
  getErrorMessage,
  newToast,
  API_URL,
} from '../../../configurations/index.config';
import type { QRAsset, Site, Checkpoint, Zone, Client } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  qrPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#faf9f8',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  qrImage: {
    width: '120px',
    height: '120px',
    border: '2px solid #edebe9',
    borderRadius: '8px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  formRow: {
    display: 'flex',
    gap: '16px',
    width: '100%',
  },
  formField: {
    flex: '1',
    minWidth: '0',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '24px',
    borderTop: '1px solid #edebe9',
  },
});

const AssignQRFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [qrAsset, setQrAsset] = useState<QRAsset | null>(null);

  const [zones, setZones] = useState<Zone[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);

  const [selectedZoneId, setSelectedZoneId] = useState<string>('');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [selectedCheckpointId, setSelectedCheckpointId] = useState<string>('');
  const [notes, setNotes] = useState('');

  const [allSites, setAllSites] = useState<Site[]>([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Clients don't have zone_id, so no need to filter them by zone
  // Filtering happens at the Site level

  useEffect(() => {
    let filteredSites = allSites;

    if (selectedZoneId) {
      filteredSites = filteredSites.filter(site => site.zone_id === Number(selectedZoneId));
    }

    if (selectedClientId) {
      filteredSites = filteredSites.filter(site => site.client_id === Number(selectedClientId));
    }

    setSites(filteredSites);
    setSelectedSiteId('');
    setSelectedCheckpointId('');
  }, [selectedZoneId, selectedClientId, allSites]);

  useEffect(() => {
    if (selectedSiteId) {
      fetchCheckpoints(Number(selectedSiteId));
    } else {
      setCheckpoints([]);
      setSelectedCheckpointId('');
    }
  }, [selectedSiteId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [qrRes, zonesRes, clientsRes, sitesRes] = await Promise.all([
        ApiClient.get(`/get-qr-asset/${id}`),
        ApiClient.get('/get-zones'),
        ApiClient.get('/get-clients'),
        ApiClient.get('/get-sites'),
      ]);

      if (qrRes.data.status === 'success') {
        setQrAsset(qrRes.data.data);
      }

      if (zonesRes.data.status === 'success') {
        setZones(zonesRes.data.data || []);
      }

      if (clientsRes.data.status === 'success') {
        const clientData = clientsRes.data.data || [];
        setClients(clientData);
      }

      if (sitesRes.data.status === 'success') {
        const siteData = sitesRes.data.data || [];
        setAllSites(siteData);
        setSites(siteData);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
      navigate('/account/qr-assets');
    }
    setLoading(false);
  };

  const fetchCheckpoints = async (siteId: number) => {
    try {
      const response = await ApiClient.get('/get-checkpoints', {
        params: { site_id: siteId, status: 'active' },
      });

      if (response.data.status === 'success') {
        setCheckpoints(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleAssign = async () => {
    if (!selectedSiteId || !selectedCheckpointId) {
      newToast('error', 'Please select both site and checkpoint');
      return;
    }

    if (!qrAsset?.qr_token) {
      newToast('error', 'QR token not found');
      return;
    }

    setAssigning(true);
    try {
      const response = await ApiClient.post('/assign-qr-to-checkpoint', {
        qr_token: qrAsset.qr_token,
        checkpoint_id: Number(selectedCheckpointId),
        notes: notes || undefined,
      });

      if (response.data.status === 'success') {
        newToast('success', 'QR code assigned successfully');
        navigate('/account/qr-assets');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setAssigning(false);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner label="Loading QR asset..." />
      </div>
    );
  }

  if (!qrAsset) {
    return null;
  }

  return (
    <PermissionGate privilege="qr-assets.assign" showDenied>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={() => navigate('/account/qr-assets')}
          />
          <Text className={styles.pageTitle}>Assign QR Code to Checkpoint</Text>
        </div>

        <ContentCard>
          <div className={styles.qrPreview}>
            <img
              src={`${API_URL}${qrAsset.qr_image_url}`}
              alt={qrAsset.qr_name}
              className={styles.qrImage}
            />
            <div>
              <Text weight="semibold" size={500} style={{ display: 'block', marginBottom: '8px' }}>
                {qrAsset.qr_name}
              </Text>
              <Text size={300} style={{ color: '#605e5c', display: 'block', marginBottom: '4px' }}>
                Batch {qrAsset.batch_number} - Sequence {qrAsset.batch_sequence}
              </Text>
              <Text
                size={200}
                style={{ color: '#605e5c', fontFamily: 'monospace', display: 'block' }}
              >
                Token: {qrAsset.qr_token.substring(0, 24)}...
              </Text>
            </div>
          </div>

          <div className={styles.form}>
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <Field label="Select Zone" hint="Optional: Filter sites by zone">
                  <SearchableDropdown
                    placeholder="All Zones"
                    selectedValue={selectedZoneId}
                    onSelect={setSelectedZoneId}
                    options={[
                      { id: '', label: 'All Zones' },
                      ...zones.map(zone => ({
                        id: zone.id.toString(),
                        label: zone.name,
                      })),
                    ]}
                  />
                </Field>
              </div>

              <div className={styles.formField}>
                <Field label="Select Client" hint="Optional: Filter sites by client">
                  <SearchableDropdown
                    placeholder="All Clients"
                    selectedValue={selectedClientId}
                    onSelect={setSelectedClientId}
                    options={[
                      { id: '', label: 'All Clients' },
                      ...clients.map(client => ({
                        id: client.id.toString(),
                        label: client.client_name,
                      })),
                    ]}
                  />
                </Field>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formField}>
                <Field label="Select Site" required>
                  <SearchableDropdown
                    placeholder="Choose a site"
                    selectedValue={selectedSiteId}
                    onSelect={setSelectedSiteId}
                    options={sites.map(site => ({
                      id: site.id.toString(),
                      label: site.name,
                    }))}
                    required
                  />
                </Field>
              </div>

              <div className={styles.formField}>
                <Field label="Select Checkpoint" required>
                  <SearchableDropdown
                    placeholder={selectedSiteId ? 'Choose a checkpoint' : 'Select a site first'}
                    selectedValue={selectedCheckpointId}
                    onSelect={setSelectedCheckpointId}
                    options={checkpoints.map(checkpoint => ({
                      id: checkpoint.id.toString(),
                      label: checkpoint.checkpoint_name,
                    }))}
                    disabled={!selectedSiteId || checkpoints.length === 0}
                    required
                  />
                </Field>
              </div>
            </div>

            {(selectedZoneId || selectedClientId) && sites.length === 0 && (
              <Text size={200} style={{ color: '#a4262c' }}>
                No sites found matching the selected filters
              </Text>
            )}

            {selectedSiteId && checkpoints.length === 0 && (
              <Text size={200} style={{ color: '#a4262c' }}>
                No active checkpoints found for this site
              </Text>
            )}

            <div className={styles.formRow}>
              <div className={styles.formField}>
                <Field label="Notes" hint="Optional assignment notes">
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      border: '1px solid #d1d1d1',
                      borderRadius: '4px',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                    }}
                  />
                </Field>
              </div>
            </div>
          </div>

          <div className={styles.actions}>
            <Button appearance="secondary" onClick={() => navigate('/account/qr-assets')}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={<CheckmarkCircleRegular />}
              onClick={handleAssign}
              disabled={assigning || !selectedSiteId || !selectedCheckpointId}
            >
              {assigning ? 'Assigning...' : 'Assign QR Code'}
            </Button>
          </div>
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default AssignQRFluentUI;
